import { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from 'prop-types';
import axios from "axios";
import { API } from "@/config";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, Filter, RefreshCw, User } from "lucide-react";
import { ACTION_LABELS, getActionStyle, formatAuditTimestamp } from "./auditUtils";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../../db";


// ==================== Component ====================

export default function ClinicAuditTab({ clinicId, token }) {
    const [auditEntries, setAuditEntries] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditHasMore, setAuditHasMore] = useState(false);
    const [auditInitialized, setAuditInitialized] = useState(false);
    const [auditActionFilter, setAuditActionFilter] = useState('');
    const [auditActorFilter, setAuditActorFilter] = useState('');
    const auditScrollRef = useRef(null);

    // Get clinic members + resolve names from users table for actor filter dropdown
    const clinicMembers = useLiveQuery(async () => {
        const members = await db.clinic_members.where('clinic_id').equals(clinicId).toArray();
        const withNames = await Promise.all(members.map(async (m) => {
            const user = await db.users.get(m.user_id);
            return { ...m, name: user?.name || m.doctor_name || 'Unknown' };
        }));
        return withNames;
    }, [clinicId]);

    // Use refs to avoid stale closures in scroll handler
    const loadingRef = useRef(false);
    const entriesRef = useRef([]);
    const hasMoreRef = useRef(false);

    const fetchAuditLogs = useCallback(async (mode = 'initial') => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setAuditLoading(true);
        try {
            const params = { limit: 50 };
            if (auditActionFilter) params.action = auditActionFilter;
            if (auditActorFilter) params.actor_id = auditActorFilter;

            const currentEntries = entriesRef.current;
            if (mode === 'older' && currentEntries.length > 0) {
                params.before = currentEntries[currentEntries.length - 1].timestamp;
            } else if (mode === 'newer' && currentEntries.length > 0) {
                params.after = currentEntries[0].timestamp;
            }

            const response = await axios.get(`${API}/clinics/${clinicId}/audit-logs`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            const { entries, has_more } = response.data;

            setAuditEntries(prev => {
                let next;
                if (mode === 'older') {
                    next = [...prev, ...entries];
                } else if (mode === 'newer') {
                    next = [...entries, ...prev];
                } else {
                    next = entries;
                }
                entriesRef.current = next;
                return next;
            });
            hasMoreRef.current = has_more;
            setAuditHasMore(has_more);
            setAuditInitialized(true);
        } catch (error) {
            console.error('Failed to load audit logs:', error);
        } finally {
            loadingRef.current = false;
            setAuditLoading(false);
        }
    }, [clinicId, token, auditActionFilter, auditActorFilter]);

    // Initial fetch
    useEffect(() => {
        if (!auditInitialized) {
            fetchAuditLogs('initial');
        }
    }, [auditInitialized, fetchAuditLogs]);

    // Infinite scroll handler
    useEffect(() => {
        const container = auditScrollRef.current;
        if (!container) return;

        const handleScroll = () => {
            const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
            if (nearBottom && hasMoreRef.current && !loadingRef.current) {
                fetchAuditLogs('older');
            }
        };
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [fetchAuditLogs]);

    return (
        <div style={{ maxWidth: '800px' }}>
            {/* Filters */}
            <div style={{
                display: 'flex', gap: '0.75rem', marginBottom: '1rem',
                alignItems: 'center', flexWrap: 'wrap'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Filter size={16} color="#64748b" />
                    <Select value={auditActionFilter || 'all'} onValueChange={(v) => {
                        setAuditActionFilter(v === 'all' ? '' : v);
                        setAuditInitialized(false);
                        setAuditEntries([]);
                        entriesRef.current = [];
                    }}>
                        <SelectTrigger style={{ width: '200px' }}>
                            <SelectValue placeholder="All Actions" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Actions</SelectItem>
                            {Object.entries(ACTION_LABELS).map(([key, label]) => (
                                <SelectItem key={key} value={key}>{label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <User size={16} color="#64748b" />
                    <Select value={auditActorFilter || 'all'} onValueChange={(v) => {
                        setAuditActorFilter(v === 'all' ? '' : v);
                        setAuditInitialized(false);
                        setAuditEntries([]);
                        entriesRef.current = [];
                    }}>
                        <SelectTrigger style={{ width: '180px' }}>
                            <SelectValue placeholder="All People" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All People</SelectItem>
                            {clinicMembers?.map(m => (
                                <SelectItem key={m.user_id} value={m.user_id}>
                                    {m.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchAuditLogs(entriesRef.current.length > 0 ? 'newer' : 'initial')}
                    disabled={auditLoading}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                >
                    <RefreshCw size={14} className={auditLoading ? 'animate-spin' : ''} />
                    Refresh
                </Button>
            </div>

            {/* Timeline */}
            <div
                ref={auditScrollRef}
                style={{
                    maxHeight: 'calc(100vh - 280px)',
                    overflowY: 'auto',
                    paddingRight: '0.5rem'
                }}
            >
                {auditEntries.length === 0 && !auditLoading && (
                    <div className="card" style={{
                        textAlign: 'center', padding: '3rem', color: '#94a3b8'
                    }}>
                        <Activity size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
                        <p style={{ fontSize: '1rem', fontWeight: '500' }}>No activity recorded yet</p>
                        <p style={{ fontSize: '0.875rem' }}>Actions performed in this clinic will appear here</p>
                    </div>
                )}
                {auditEntries.map((entry, idx) => {
                    const style = getActionStyle(entry.action);
                    const actionLabel = ACTION_LABELS[entry.action] || entry.action;
                    return (
                        <div
                            key={entry.id || idx}
                            style={{
                                display: 'flex',
                                gap: '1rem',
                                marginBottom: '0.25rem',
                                position: 'relative'
                            }}
                        >
                            {/* Timeline line + dot */}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: '20px',
                                flexShrink: 0
                            }}>
                                <div style={{
                                    width: '10px', height: '10px', borderRadius: '50%',
                                    background: style.dot, marginTop: '1.25rem', flexShrink: 0,
                                    boxShadow: `0 0 0 3px ${style.bg}`
                                }} />
                                {idx < auditEntries.length - 1 && (
                                    <div style={{
                                        width: '2px', flex: 1, background: '#e2e8f0',
                                        minHeight: '1rem'
                                    }} />
                                )}
                            </div>

                            {/* Entry card */}
                            <div className="card" style={{
                                flex: 1, padding: '0.875rem 1rem',
                                marginBottom: '0.5rem',
                                borderLeft: `3px solid ${style.dot}`
                            }}>
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'flex-start', gap: '0.5rem',
                                    flexWrap: 'wrap'
                                }}>
                                    <div>
                                        <span style={{
                                            display: 'inline-block',
                                            padding: '0.125rem 0.5rem',
                                            borderRadius: '9999px',
                                            fontSize: '0.7rem',
                                            fontWeight: '600',
                                            background: style.bg,
                                            color: style.color,
                                            marginBottom: '0.375rem'
                                        }}>
                                            {actionLabel}
                                        </span>
                                        <p style={{
                                            margin: 0, fontSize: '0.875rem',
                                            color: '#334155', lineHeight: 1.5
                                        }}>
                                            {entry.summary}
                                        </p>
                                    </div>
                                    <span style={{
                                        fontSize: '0.75rem', color: '#94a3b8',
                                        whiteSpace: 'nowrap', flexShrink: 0
                                    }}>
                                        {formatAuditTimestamp(entry.timestamp)}
                                    </span>
                                </div>
                                <div style={{
                                    marginTop: '0.375rem', fontSize: '0.75rem',
                                    color: '#94a3b8', display: 'flex', gap: '0.75rem'
                                }}>
                                    <span>by {entry.actor_name || 'Unknown'}</span>
                                    {entry.actor_role && (
                                        <span style={{ textTransform: 'capitalize' }}>• {entry.actor_role}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {auditLoading && (
                    <div style={{
                        textAlign: 'center', padding: '1.5rem', color: '#94a3b8'
                    }}>
                        Loading...
                    </div>
                )}
                {!auditLoading && auditEntries.length > 0 && !auditHasMore && (
                    <div style={{
                        textAlign: 'center', padding: '1rem',
                        color: '#cbd5e1', fontSize: '0.8rem'
                    }}>
                        — End of audit log —
                    </div>
                )}
            </div>
        </div>
    );
}

ClinicAuditTab.propTypes = {
    clinicId: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
};
